// The screen switch above a venue's (or every venue's) content, as an ARIA
// tabs widget: one tab in the tab order, arrow keys / Home / End move and
// select (automatic activation), and the panel below is labelled by the
// selected tab. Render the screen's content inside <ScreenPanel>.

import { useId, useRef, type ReactNode } from "react";

import { rovingIndex } from "../roving";

export interface ScreenTabsIds {
  tab: (id: string) => string;
  panel: string;
}

/** Stable element ids shared by a tab list and its panel. */
export function useScreenTabsIds(): ScreenTabsIds {
  const base = useId();
  return { tab: (id) => `${base}-tab-${id}`, panel: `${base}-panel` };
}

export function ScreenTabs<T extends string>({
  ids,
  label,
  tabs,
  current,
  onSelect,
}: {
  ids: ScreenTabsIds;
  label: string;
  tabs: ReadonlyArray<readonly [T, string]>;
  current: T;
  onSelect: (id: T) => void;
}) {
  const refs = useRef(new Map<T, HTMLButtonElement>());
  const index = tabs.findIndex(([id]) => id === current);
  return (
    <div
      aria-label={label}
      className="tabs"
      onKeyDown={(event) => {
        const next = rovingIndex(event.key, index, tabs.length, "horizontal");
        if (next === null) return;
        event.preventDefault();
        const id = tabs[next]![0];
        onSelect(id);
        refs.current.get(id)?.focus();
      }}
      role="tablist"
    >
      {tabs.map(([id, text]) => {
        const active = id === current;
        return (
          <button
            aria-controls={active ? ids.panel : undefined}
            aria-selected={active}
            className="tab"
            data-active={active}
            id={ids.tab(id)}
            key={id}
            onClick={() => onSelect(id)}
            ref={(element) => {
              if (element) refs.current.set(id, element);
              else refs.current.delete(id);
            }}
            role="tab"
            tabIndex={active ? 0 : -1}
            type="button"
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}

export function ScreenPanel({ ids, current, children }: { ids: ScreenTabsIds; current: string; children: ReactNode }) {
  return (
    <div aria-labelledby={ids.tab(current)} className="tab-panel" id={ids.panel} role="tabpanel" tabIndex={0}>
      {children}
    </div>
  );
}
